// WarningFilterDialog.tsx
import { useState, ReactNode } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2 as Grid,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  ListSubheader,
  Checkbox,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  ANY_SEVERITY_LABEL,
  arraysEqualAsSets,
  HIGH_SEVERITY_NAME,
  LOW_SEVERITY_NAME,
  MED_SEVERITY_NAME,
  MED_TO_HIGH_SEVERITY_LABEL,
  type Options,
  type WarningFilterValues,
} from "./WarningFilterChips";
import {
  WARNING_SEVERITIES,
  WARNING_CATEGORY_LABELS,
  type WarningCategory,
} from "@caredna-marlin/utils/warnings";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import Help from "~/components/common/Help";

type Props = {
  onClose: () => void;
  values: WarningFilterValues; // now includes `warningCategories: WarningCategory[]`
  onSave: (v: WarningFilterValues) => void;

  visitTypeOptions: Options;
  patientOptions: Options;
  providerOptions: Options;
};

type FilterGridRowProps = {
  label: ReactNode | string;
  loading?: boolean;
  helpIcon?: ReactNode;
  children?: ReactNode;
};

function FilterGridRow({ label, loading = false, helpIcon, children }: FilterGridRowProps) {
  return (
    <>
      <Grid size={3} alignSelf="center">
        <Box display="flex" alignItems="center">
          {typeof label === "string" ? <Typography>{label}</Typography> : label}
          {helpIcon}
        </Box>
      </Grid>
      <Grid size={9}>{loading ? <Skeleton variant="text" sx={{ mt: 1 }} /> : children}</Grid>
    </>
  );
}

// Default: all categories with severity >= 2
const DEFAULT_WARNING_CATEGORIES: WarningCategory[] = (Object.keys(
  WARNING_SEVERITIES
) as WarningCategory[]).filter((k) => WARNING_SEVERITIES[k] >= 2);

// helpers
const bySeverity = (level: 0 | 1 | 2 | 3) =>
  (Object.keys(WARNING_SEVERITIES) as WarningCategory[]).filter(
    (k) => WARNING_SEVERITIES[k] === level
  );

export default function WarningFilterDialog({
  onSave,
  onClose,
  values,
  visitTypeOptions,
  patientOptions,
  providerOptions,
}: Props) {
  const [local, setLocal] = useState<WarningFilterValues>(values);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      slotProps={{
        paper: {
          component: "form",
          sx: { p: 2, width: 640, maxWidth: "95vw" },
        },
      }}
    >
      <DialogContent>
        <Grid container spacing={2}>
          {/* Visit types */}
          <FilterGridRow label="Visit types">
            <Select
              fullWidth
              displayEmpty
              multiple
              value={local.visitTypeIds}
              onChange={({ target: { value } }) =>
                setLocal({
                  ...local,
                  visitTypeIds: typeof value === "string" ? value.split(",") : (value as string[]),
                })
              }
              renderValue={(selected) => {
                const sel = selected as string[];
                if (sel.length === 0) return <span>Any</span>;
                return sel.map((val) => visitTypeOptions.get(val) ?? val).join(", ");
              }}
            >
              {menuItemsFromMap(visitTypeOptions)}
            </Select>
          </FilterGridRow>

          {/* Patients */}
          <FilterGridRow label="Patients">
            <Select
              fullWidth
              displayEmpty
              multiple
              value={local.patientIds}
              onChange={({ target: { value } }) =>
                setLocal({
                  ...local,
                  patientIds: typeof value === "string" ? value.split(",") : (value as string[]),
                })
              }
              renderValue={(selected) => {
                const sel = selected as string[];
                if (sel.length === 0) return <span>Any</span>;
                return sel.map((val) => patientOptions.get(val) ?? val).join(", ");
              }}
            >
              {menuItemsFromMap(patientOptions)}
            </Select>
          </FilterGridRow>

          {/* Providers */}
          <FilterGridRow label="Providers">
            <Select
              fullWidth
              displayEmpty
              multiple
              value={local.providerIds}
              onChange={({ target: { value } }) =>
                setLocal({
                  ...local,
                  providerIds: typeof value === "string" ? value.split(",") : (value as string[]),
                })
              }
              renderValue={(selected) => {
                const sel = selected as string[];
                if (sel.length === 0) return <span>Any</span>;
                return sel.map((val) => providerOptions.get(val) ?? val).join(", ");
              }}
            >
              {menuItemsFromMap(providerOptions)}
            </Select>
          </FilterGridRow>

          {/* Categories */}
          <FilterGridRow
            label="Warning types"
            helpIcon={<SeverityHelp />}
          >
            
            <Select
              fullWidth
              multiple
              displayEmpty
              value={local.warningCategories}
              onChange={({ target: { value } }) =>
                setLocal({
                  ...local,
                  warningCategories:
                    typeof value === "string"
                      ? (value.split(",") as WarningCategory[])
                      : value,
                })
              }
              renderValue={(selected) => {
                if (selected.length === 0) return <span>{ANY_SEVERITY_LABEL}</span>;
                return arraysEqualAsSets(selected, DEFAULT_WARNING_CATEGORIES) ? (
                  <span>{MED_TO_HIGH_SEVERITY_LABEL}</span>
                ) : (
                  <span>{selected.map((c) => WARNING_CATEGORY_LABELS[c]).join(", ")}</span>
                );
              }}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 400 } }, MenuListProps: { autoFocusItem: false }, }}
            >
              {/* Quick actions header */}
              <ListSubheader disableSticky>
                <Box display="flex" gap={1} alignItems="center" px={1} py={0.5}>
                  <Button
                    size="small"
                    onMouseDown={(e) => e.preventDefault()} // keep menu open
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocal({ ...local, warningCategories: [] });
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocal({ ...local, warningCategories: DEFAULT_WARNING_CATEGORIES });
                    }}
                  >
                    Default
                  </Button>
                </Box>
              </ListSubheader>
              <Divider />

              {/* Section: Critical (3) */}
              <ListSubheader disableSticky>{HIGH_SEVERITY_NAME}</ListSubheader>
              {bySeverity(3).map((cat) => {
                const checked = local.warningCategories.includes(cat);
                return (
                  <MenuItem key={cat} value={cat}>
                    <Checkbox checked={checked} />
                    <ListItemText primary={WARNING_CATEGORY_LABELS[cat]} />
                  </MenuItem>
                );
              })}

              {/* Section: Warnings (2) */}
              <ListSubheader disableSticky>{MED_SEVERITY_NAME}</ListSubheader>
              {bySeverity(2).map((cat) => {
                const checked = local.warningCategories.includes(cat);
                return (
                  <MenuItem key={cat} value={cat}>
                    <Checkbox checked={checked} />
                    <ListItemText primary={WARNING_CATEGORY_LABELS[cat]} />
                  </MenuItem>
                );
              })}

              {/* Section: Info (1) */}
              <ListSubheader disableSticky>{LOW_SEVERITY_NAME}</ListSubheader>
              {bySeverity(1).map((cat) => {
                const checked = local.warningCategories.includes(cat);
                return (
                  <MenuItem key={cat} value={cat}>
                    <Checkbox checked={checked} />
                    <ListItemText primary={WARNING_CATEGORY_LABELS[cat]} />
                  </MenuItem>
                );
              })}
            </Select>
          </FilterGridRow>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(local);
            onClose();
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const menuItemsFromMap = (options: Options) =>
  [...options.entries()]
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([value, label]) => (
      <MenuItem key={value} value={value}>
        {label}
      </MenuItem>
    ));

const SeverityHelp = () => {
  return (
    <Help
      slotProps={{
        tooltip: {
          sx: {
            p: 2,
            bgcolor: "white",
            boxShadow: 4,
            borderRadius: 1,
            maxWidth: "35em",
            minWidth: 420,
            color: "inherit",
          },
        },
      }}
      text={
        <Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
            <EventBusyIcon fontSize="small" color="error" sx={{ mt: 0.25, mr: 1 }} />
            <Typography variant="body2">
              <strong>{HIGH_SEVERITY_NAME}:</strong> Issues that may prevent the visit from
              happening.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
            <WarningAmberRoundedIcon fontSize="small" color="warning" sx={{ mt: 0.25, mr: 1 }} />
            <Typography variant="body2">
              <strong>{MED_SEVERITY_NAME}:</strong> Issues that may impact patient experience.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25, mr: 1 }} />
            <Typography variant="body2">
              <strong>{LOW_SEVERITY_NAME}:</strong> Issues that may create operational
              inefficiencies.
            </Typography>
          </Box>
        </Box>
      }
    />
  );
};